import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { images, brand } from "@/i18n/translations";
import { Reveal } from "@/components/site/Reveal";
import { Socials } from "@/components/site/Socials";

const CTA = () => {
  const { t } = useLang();
  const c = t.contact;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="relative overflow-hidden bg-ink"
    >
      <motion.img
        style={{ y: imgY, scale: 1.2 }}
        src={images.contact}
        alt="Open plain at golden hour"
        className="absolute inset-0 h-full w-full object-cover opacity-40 will-change-transform"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/50" />

      <div className="relative z-10 mx-auto flex max-w-[1200px] flex-col items-start px-5 py-32 md:items-center md:px-10 md:py-48 md:text-center">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-rust">
            {c.overline}
          </span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-3xl font-display text-5xl font-medium uppercase leading-[0.9] tracking-tight text-bone md:text-8xl">
            {c.title}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 max-w-xl font-body text-lg leading-relaxed text-bone/70">
            {c.body}
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <a
            href="#cerere"
            data-testid="contact-cta"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-bone px-9 py-4 font-body text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-colors duration-500 hover:bg-rust hover:text-bone"
          >
            {c.cta}
            <span className="transition-transform duration-500 group-hover:translate-x-1">
              →
            </span>
          </a>
        </Reveal>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLang();
  const f = t.footer;
  return (
    <footer data-testid="site-footer" className="bg-olive text-bone">
      <div className="mx-auto max-w-[1500px] px-5 py-20 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3.5">
              <img
                src={brand.logo}
                alt="Pro Beniesto — Hunting Agency Romania"
                className="h-16 w-auto"
              />
              <span className="flex flex-col leading-none">
                <span className="font-display text-2xl font-semibold uppercase tracking-[0.14em]">
                  {brand.name}
                </span>
                <span className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.24em] text-bone/60">
                  {brand.tag}
                </span>
              </span>
            </div>
            <p className="mt-5 max-w-xs font-body text-sm leading-relaxed text-bone/70">
              {f.tagline}
            </p>
            <p className="mt-8 font-script text-3xl text-bone/90">
              {t.hero.script}
            </p>
            <Socials testId="footer-socials" className="mt-8 text-bone/90" />
          </div>

          {f.cols.map((col) => (
            <div key={col.title}>
              <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-bone/50">
                {col.title}
              </h4>
              <ul className="mt-5 flex flex-col gap-3">
                {col.links.map((l) => (
                  <li key={l}>
                    <a
                      href="#top"
                      className="font-body text-sm text-bone/85 transition-colors duration-300 hover:text-bone"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-bone/20 pt-8 md:flex-row md:items-center md:justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/60">
            © {new Date().getFullYear()} {brand.name}. {f.rights}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/60">
            {t.nav.phone}
          </span>
        </div>
      </div>
    </footer>
  );
};

export const ContactFooter = () => (
  <>
    <CTA />
    <Footer />
  </>
);
